import { Link } from 'react-router-dom';
import type { TopicWithMeta } from '@/lib/community';
import { excerpt, formatRelativeTime, formatCount } from '@/lib/format';
import UserAvatar from './UserAvatar';
import CategoryBadge from './CategoryBadge';

interface TopicListCardProps {
  topic: TopicWithMeta;
  showCover?: boolean;
}

export default function TopicListCard({ topic, showCover = true }: TopicListCardProps) {
  return (
    <article className="group flex gap-4 p-4 md:p-5 bg-background-50 border border-background-200 rounded-lg transition-colors hover:border-primary-300 hover:bg-background-100/70">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <CategoryBadge name={topic.category.name} color={topic.category.color} icon={topic.category.icon} />
          {topic.isHot && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 text-accent-700 border border-accent-200 px-2 py-1 text-xs font-label font-bold">
              <i className="ri-fire-fill text-[13px]" />
              热门
            </span>
          )}
        </div>

        <Link to={`/topic/${topic.id}`} className="cursor-pointer">
          <h3 className="text-base md:text-lg font-bold text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors">
            {topic.title}
          </h3>
        </Link>

        <p className="mt-1.5 text-sm text-foreground-500 leading-relaxed line-clamp-2">{excerpt(topic.summary, 96)}</p>

        <div className="mt-3 flex items-center gap-3 md:gap-4 flex-wrap text-xs text-foreground-500">
          <Link to={`/user/${topic.author.id}`} className="flex items-center gap-2 cursor-pointer hover:text-primary-700 transition-colors">
            <UserAvatar src={topic.author.avatar} alt={topic.author.name} size="xs" />
            <span className="font-label font-bold">{topic.author.name}</span>
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1">
            <i className="ri-time-line" />
            {formatRelativeTime(topic.createdAt)}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-chat-3-line" />
            {formatCount(topic.replyCount)} 回复
          </span>
          <span className="hidden xs:inline-flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatCount(topic.viewCount)}
          </span>
        </div>
      </div>

      {showCover && (
        <Link
          to={`/topic/${topic.id}`}
          className="hidden sm:block w-28 lg:w-36 h-24 lg:h-28 rounded-lg overflow-hidden shrink-0 bg-background-200 cursor-pointer"
        >
          <img
            src={topic.cover}
            alt={topic.title}
            title={`${topic.title} - 蜜糖社区话题`}
            className="w-full h-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
      )}
    </article>
  );
}